import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { Button } from "../components/ui/button";
import { Loader2, Target, CheckCircle, Clock, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_CFG = {
  pending:     { cls: "bg-gray-100 text-gray-600",    icon: Clock },
  in_progress: { cls: "bg-blue-100 text-blue-700",    icon: Target },
  completed:   { cls: "bg-green-100 text-green-700",  icon: CheckCircle },
  cancelled:   { cls: "bg-red-100 text-red-500",      icon: AlertCircle },
};

const CATEGORY_EMOJIS = {
  skill: "🎯", behavior: "🤝", experience: "🌟", certification: "📜", other: "💡",
};

function GoalCard({ goal, onUpdateProgress }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [localPct, setLocalPct] = useState(goal.progress_pct || 0);
  const [saving, setSaving] = useState(false);
  const statusKey = STATUS_CFG[goal.status] ? goal.status : "pending";
  const cfg = STATUS_CFG[statusKey];
  const Icon = cfg.icon;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdateProgress(goal.id, localPct);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-xl border p-4 ${goal.status === "completed" ? "opacity-75" : ""}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <span className="text-lg mt-0.5">{CATEGORY_EMOJIS[goal.category] || "💡"}</span>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">{goal.title}</div>
            {goal.due_date && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("portal.idp.dueDate")}: {goal.due_date}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${cfg.cls}`}>
            <Icon className="h-3 w-3" />{t(`portal.idp.status.${statusKey}`)}
          </span>
          <button onClick={() => setExpanded(e => !e)}>
            {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-2">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>{t("portal.idp.progress")}</span>
          <span className="font-medium">{localPct}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${localPct === 100 ? "bg-green-500" : "bg-blue-500"}`}
            style={{ width: `${localPct}%` }}
          />
        </div>
      </div>

      {expanded && goal.status !== "completed" && (
        <div className="mt-3 space-y-2">
          {goal.description && <p className="text-xs text-muted-foreground">{goal.description}</p>}
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={localPct}
              onChange={e => setLocalPct(parseInt(e.target.value))}
              className="flex-1 accent-blue-500"
            />
            <span className="text-sm font-bold w-10 text-right">{localPct}%</span>
          </div>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || localPct === goal.progress_pct}
            className="w-full"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            {saving ? t("portal.idp.saving") : t("portal.idp.updateProgress")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function MonIDP() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePlan, setActivePlan] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("v1/career/plans/me").catch(() => ({ data: [] }));
      const all = r.data || [];
      setPlans(all);
      const active = all.find(p => p.status === "active") || all[0] || null;
      setActivePlan(active);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateProgress = async (goalId, pct) => {
    await apiClient.patch(`v1/career/goals/${goalId}`, { progress_pct: pct, status: pct === 100 ? "completed" : pct > 0 ? "in_progress" : "pending" });
    load();
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("common.loading")}
    </div>
  );

  if (plans.length === 0) return (
    <div className="text-center py-16 text-muted-foreground">
      <Target className="h-10 w-10 mx-auto mb-2 text-gray-300" />
      <p>{t("portal.idp.emptyPlan")}</p>
      <p className="text-xs mt-1">{t("portal.idp.emptyPlanHint")}</p>
    </div>
  );

  const completedGoals = activePlan?.goals?.filter(g => g.status === "completed").length || 0;
  const totalGoals = activePlan?.goals?.length || 0;
  const overallPct = totalGoals ? Math.round((completedGoals / totalGoals) * 100) : 0;

  return (
    <div className="space-y-5">
      {plans.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {plans.map(p => (
            <button
              key={p.id}
              onClick={() => setActivePlan(p)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activePlan?.id === p.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            >
              {p.title}
            </button>
          ))}
        </div>
      )}

      {activePlan && (
        <>
          <div className="bg-card rounded-xl border p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold">{activePlan.title}</h3>
                {activePlan.target_level && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("portal.idp.target")}: {activePlan.target_level.title}
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{overallPct}%</div>
                <div className="text-xs text-muted-foreground">{completedGoals}/{totalGoals} {t("portal.idp.goals")}</div>
              </div>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${overallPct}%` }} />
            </div>
            {activePlan.start_date && activePlan.end_date && (
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>{activePlan.start_date}</span>
                <span>{activePlan.end_date}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("portal.idp.goalsTitle")} ({totalGoals})
            </h3>
            {(activePlan.goals || []).map(goal => (
              <GoalCard key={goal.id} goal={goal} onUpdateProgress={updateProgress} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
